import { APP_CONFIG }        from '@appConfig/_app-config_master';
import { APP_MODE }          from '@appConfig/app-mode-config';
import { IActionItem }       from '@core/interfaces/entity.interface';
import { EntityVariantEnum } from '@models/entity.model';
import { ObjectUtils }       from '@utils/helpers/index';


export {
	executeByEntityVariant,
	getActionItemByConditionCheck,
	getValueByEntityVariant
};


/**
 * Sets the state of an action item and returns it.
 *
 * @param actionItemConditionCheckFunction - A callback function that performs
 *                                           the conditional check, sets the
 *                                           `IActionItem` object accordingly,
 *                                           and returns it if the check is not skipped.
 * @param entityActionProp - Identifier for the action item to check.
 * @param skipEntityActionChecks - An array holding the identifiers of those
 *                                 action items that should bypass the conditional
 *                                 check; if nullish or empty, no skipping occurs.
 * @returns An `IActionItem` object for a disabled action if the check is to be
 *          skipped, otherwise the `IActionItem` object as set by the callback function
 */
function getActionItemByConditionCheck<EntityActionProp extends string>(
	actionItemConditionCheckFunction: () => IActionItem,
	entityActionProp: EntityActionProp,
	skipEntityActionChecks: EntityActionProp[]
): IActionItem {

	const skippedActionItem: IActionItem = {
		isEnabled: false,
		messages: {
			show: false,
			items: []
		},
		showLoader: false
	};

	if ( !actionItemConditionCheckFunction || !entityActionProp ) { return skippedActionItem; }

	return skipEntityActionChecks?.includes( entityActionProp )
				 ? skippedActionItem
				 : actionItemConditionCheckFunction();
}


/**
 * Executes a callback function according to the entity variant type.
 *
 * @param entityVariant - The variant of the entity, determining which function
 *                        to execute. If this value is nullish, neither function is executed.
 * @param externalFn - The function to execute if the entity is identified as `EXTERNAL`.
 * @param internalFn - The function to execute if the entity is identified as `INTERNAL`.
 *
 * This function does not return a value but causes a side effect based on the entity variant.
 */
function executeByEntityVariant(
	entityVariant: EntityVariantEnum,
	externalFn: () => void,
	internalFn: () => void
): void {
	if ( ObjectUtils.isNullish( entityVariant ) ) {
		if ( APP_CONFIG.appMode === APP_MODE.LOCAL ) { // log only in local dev environment
		console.warn( 'EntityUtils:executeByEntityVariant -> entityVariant is nullish' );
	}
		return;
	}

	if (
		entityVariant === EntityVariantEnum.EXTERNAL && !externalFn ||
		entityVariant === EntityVariantEnum.INTERNAL && !internalFn
	) {
		if ( APP_CONFIG.appMode === APP_MODE.LOCAL ) { // log only in local dev environment
			console.warn( `EntityUtils:executeByEntityVariant -> no callback provided for entity variant ${ entityVariant }!` );
		}
		return;
	}

	switch ( entityVariant ) {
		case EntityVariantEnum.EXTERNAL:
			externalFn();
			break;
		case EntityVariantEnum.INTERNAL:
			internalFn();
			break;
	}
}


/**
 * Returns a value according to the specified entity variant type.
 *
 * @param entityVariant - Enum value representing the variant of the entity.
 * @param externalValue - The value to return if the entity variant is EXTERNAL.
 * @param internalValue - The value to return if the entity variant is INTERNAL.
 *
 * @returns the corresponding value for the variant provided; null if entityVariant is somehow invalid (e.g. nullish).
 */
function getValueByEntityVariant<externalVariant, internalVariant>(
	entityVariant: EntityVariantEnum,
	externalValue: externalVariant,
	internalValue: internalVariant
): externalVariant | internalVariant {

	switch ( entityVariant ) {
		case EntityVariantEnum.EXTERNAL:
			return externalValue;
		case EntityVariantEnum.INTERNAL:
			return internalValue;
		default:
			if ( APP_CONFIG.appMode === APP_MODE.LOCAL ) { // log only in local dev environment
				console.warn( `EntityUtils:getValueByEntityVariant -> invalid entityVariant ${ entityVariant }` );
			}
			return null;
	}
}
